import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useMemo } from "react";
import { FuelMenuNav } from "@/components/fuel-activity/FuelMenuNav";
import { FuelMonthSummaryCards } from "@/components/fuel-activity/FuelMonthSummaryCards";
import { FuelYearPicker } from "@/components/fuel-activity/FuelYearPicker";
import { PageHeader } from "@/components/ui/PageHeader";
import { ExplorerSkeleton } from "@/components/ui/PageSkeleton";
import { getFuelMonthSummaries, getFuelYearSummaries } from "@/lib/fuel-analytics";
import { useFuelActivityData } from "@/lib/queries";

const pageVariants = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -4 },
};

export function FuelSpendYearsPage() {
  const { data, isLoading, error } = useFuelActivityData();

  const yearSummaries = useMemo(() => {
    if (!data) return [];
    return getFuelYearSummaries(data.logs);
  }, [data]);

  const monthSummaries = useMemo(() => {
    if (!data) return [];
    return getFuelMonthSummaries(data.logs);
  }, [data]);

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      className="space-y-6"
    >
      <Link
        to="/fuel-activity"
        className="inline-flex text-sm font-semibold text-muted transition hover:text-foreground"
      >
        ← User fuel activity
      </Link>

      <PageHeader
        title="Spend breakdown"
        description="Browse fuel spend by month or year across all users."
        showGreeting={false}
      />

      <FuelMenuNav />

      {isLoading ? (
        <ExplorerSkeleton title="Spend breakdown" />
      ) : error || !data ? (
        <p className="text-sm text-red-600">Failed to load spend data.</p>
      ) : (
        <div className="space-y-8">
          <section className="space-y-4">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-muted">By month</p>
              <p className="mt-0.5 text-sm text-muted">Select a month to open its full breakdown.</p>
            </div>
            <FuelMonthSummaryCards months={monthSummaries} basePath="/fuel-activity/spend" />
          </section>

          <section className="space-y-4">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-muted">By year</p>
              <p className="mt-0.5 text-sm text-muted">Select a year for the annual overview and monthly rollup.</p>
            </div>
            <FuelYearPicker years={yearSummaries} basePath="/fuel-activity/spend" />
          </section>
        </div>
      )}
    </motion.div>
  );
}
